import type { ChannelBroadcastRequest } from '@pulsify/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { channelAdminApi } from '~/entities/channel/api/channel'
import { useSupabaseSession } from '~/entities/session/model/use-auth'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { log } from '~/shared/lib/logger'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { storageManager } from '~/shared/lib/storage-manager'

export function useChannelAudience(city: string) {
  const session = useSupabaseSession()
  const term = city.trim()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_CHANNEL, 'audience', term),
    queryFn: () => channelAdminApi.audience(term || undefined),
    enabled: !!session && storageManager.isAdmin(),
  })
}

export function useChannelBroadcasts() {
  const session = useSupabaseSession()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_CHANNEL, 'broadcasts'),
    queryFn: () => channelAdminApi.list(),
    enabled: !!session && storageManager.isAdmin(),
    // Delivery runs asynchronously server-side; keep polling while any
    // broadcast is still in flight so the sent counter fills in live.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((b) => b.completedAt == null) ? 4000 : false,
  })
}

export function useChannelInbox() {
  const session = useSupabaseSession()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_CHANNEL, 'inbox'),
    queryFn: () => channelAdminApi.inbox(),
    enabled: !!session && storageManager.isAdmin(),
    refetchInterval: 15000,
  })
}

export function useChannelThread(conversationId: string | null) {
  const session = useSupabaseSession()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_CHANNEL, 'inbox', conversationId ?? ''),
    queryFn: () => channelAdminApi.thread(conversationId ?? ''),
    enabled: !!session && storageManager.isAdmin() && conversationId != null,
    refetchInterval: 10000,
  })
}

export function useSendChannelReply(conversationId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (message: string) => {
      if (!conversationId) throw new Error('No conversation selected')
      return channelAdminApi.reply(conversationId, message)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.ADMIN_CHANNEL, 'inbox') })
      toast.success(m.channelReplySent())
    },
    onError: (error: unknown) => {
      log.error(error as Error, '[Channel] Reply failed')
      toast.error(getErrorMessageFromError(error))
    },
  })
}

export function useSendChannelBroadcast() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ChannelBroadcastRequest) => channelAdminApi.send(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.ADMIN_CHANNEL) })
      toast.success(m.channelSent())
    },
    onError: (error: unknown) => {
      log.error(error as Error, '[Channel] Broadcast failed')
      toast.error(getErrorMessageFromError(error))
    },
  })
}
